/**
 * System Properties — Centralized Status Definitions
 * 
 * These are the "built-in" properties for Projects and Tasks.
 * Users can customize them (add, rename, recolor) but can never
 * delete ALL options — at least one default must remain.
 * 
 * Customizations are stored in user_preferences and merged at read time.
 */

import { resolveEntityColor } from '../utils/entityColor';

// ── Project Status Defaults ──────────────────────────────────────
export const DEFAULT_PROJECT_STATUSES = [
    { key: 'planned', label: 'Planned', color: 'categorical-2', emoji: '💡', isSystem: true },
    { key: 'active', label: 'Active', color: 'categorical-1', emoji: '🟢', isSystem: true },
    { key: 'completed', label: 'Completed', color: 'categorical-6', emoji: '✅', isSystem: true },
    { key: 'archived', label: 'Archived', color: 'categorical-10', emoji: '📦', isSystem: true },
];

// ── Task Status Defaults ─────────────────────────────────────────
export const DEFAULT_TASK_STATUSES = [
    { key: 'todo', label: 'To Do', color: 'categorical-10', isSystem: true },
    { key: 'in-progress', label: 'In Progress', color: 'categorical-3', isSystem: true },
    { key: 'done', label: 'Done', color: 'categorical-1', isSystem: true },
];

// ── Task Priority Defaults ───────────────────────────────────────
export const DEFAULT_TASK_PRIORITIES = [
    { key: 'high', label: 'High', color: 'categorical-4' },
    { key: 'medium', label: 'Medium', color: 'categorical-3' },
    { key: 'low', label: 'Low', color: 'categorical-10' },
];

// ── Merge Helpers ────────────────────────────────────────────────

/**
 * Merge default system statuses with user customizations.
 * 
 * Customizations can:
 * - Override label/color/emoji of existing keys
 * - Add new keys (user-created statuses)
 * - Mark keys as hidden (soft-delete, preserving data integrity)
 * 
 * @param {Array} defaults - The default system statuses
 * @param {Array|null} customizations - User's custom status array (from user_preferences)
 * @returns {Array} Merged status list
 */
export function mergeStatuses(defaults, customizations) {
    if (!customizations || customizations.length === 0) return defaults;

    // Build a map of customizations by key for O(1) lookup
    const customMap = new Map(customizations.map(c => [c.key, c]));
    
    // Start with defaults, applying any overrides
    const merged = defaults.map(d => {
        const custom = customMap.get(d.key);
        if (custom) {
            customMap.delete(d.key); // consumed
            return { ...d, ...custom, isSystem: true }; // system flag always stays
        }
        return d;
    });

    // Append any user-created statuses (new keys not in defaults)
    for (const [, custom] of customMap) {
        merged.push({ ...custom, isSystem: false });
    }

    return merged;
}

/**
 * Get the effective project statuses (defaults + user customizations).
 */
export function getProjectStatuses(userPrefs) {
    return mergeStatuses(DEFAULT_PROJECT_STATUSES, userPrefs?.customProjectStatuses);
}

/**
 * Get the effective task statuses (defaults + user customizations).
 */
export function getTaskStatuses(userPrefs) {
    return mergeStatuses(DEFAULT_TASK_STATUSES, userPrefs?.customTaskStatuses);
}

/**
 * Get the effective task priorities (defaults + user customizations).
 */
export function getTaskPriorities(userPrefs) {
    return mergeStatuses(DEFAULT_TASK_PRIORITIES, userPrefs?.customTaskPriorities);
}

// ── Validation ───────────────────────────────────────────────────

/**
 * Validate that at least one status option remains.
 * Returns true if the deletion is allowed.
 */
export function canDeleteStatus(statuses, keyToDelete) {
    const remaining = statuses.filter(s => s.key !== keyToDelete);
    return remaining.length >= 1;
}

/**
 * Find status info by key, with fallback for unknown statuses.
 */
export function findStatus(statuses, key) {
    return statuses.find(s => s.key === key) || { key, label: key, color: 'categorical-10', emoji: '❓' };
}

/**
 * Convert a status array to the STATUS_LABELS map format used by AllTasks.
 */
export function toStatusLabelsMap(statuses) {
    const map = {};
    statuses.forEach(s => {
        // Resolved HERE, not at the call sites. These maps exist to be poured straight
        // into CSS, and a stored colour is now an EntityColor token key — 'categorical-4'
        // is not a colour, so an unresolved map yields invalid declarations that the
        // browser drops. Resolving at the converter fixes every consumer at once
        // (AllTasks, ProjectDetail, ProjectPeekPanel).
        map[s.key] = { label: s.label, color: resolveEntityColor(s.color, 'var(--text-muted)') };
    });
    return map;
}

/**
 * Convert a priorities array to the PRIORITY_COLORS map.
 */
export function toPriorityColorsMap(priorities) {
    const map = {};
    priorities.forEach(p => {
        map[p.key] = resolveEntityColor(p.color, 'var(--text-muted)');
    });
    return map;
}
